/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { AnalyticsConstants, Diagnostics, Payload, VU } from '@tivocorp/tivo-webapp-loggers';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NetworkManager } from '../../core/network/NetworkManager';
import { RootState } from '../../store/root.reducers';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { DescriptionList } from './DescriptionList';
import { Menu } from './Menu';
import { SettingsHeader } from './SettingsHeader';
import { onCursorStateChangeSettingsOperations } from '../../store/settings/settings.reducers';
import { SettingsState } from '../../store/settings/settingsstate';
import { ServicesClientImpl } from '../../core/impls/ServicesClientImpl';
import { DeviceModel } from '../../core/device/DeviceModel';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { disableSubmenuHighlight } from '../../store/settings/accountsoverlays/accountsoverlays.reducers';
import { AntiburnOsd } from '../common';
import { MenuLanguages } from './MenuLanguages';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { Log } from '../../core/logging/Log';
import { Spinner } from '../VideoPlayerUI/Spinner';
import { SettingsMenuTypes } from './SettingsMenuTypes';
import { CursorManager } from '../../core/navigation';
import { SettingsVideoWindow } from './SettingsVideoWindow';
import {
    setDefaultSettingsMenuStateAction,
    setDefaultSettingsSubmenuStateAction,
} from '../../store/settings/settings.actions';
import { Settings } from '../../core/device/Settings';

import './SettingsScreen.scss';

const SettingsScreen: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'SettingsScreen';
    const {
        menuVerticalPosition,
        isFocused,
        submenuVerticalPosition,
        isSubmenuFocused,
        focusedItemIndex,
    } = useSelector((state: RootState) => state.settings);
    const { isOverlayDialogActive } = useSelector((state: RootState) => state.overlayDialog);
    const { isActive: isMenuLanguageActive } = useSelector(
        (state: RootState) => state.menulanguage,
    );
    const { isActive: isVideoWindowActive } = useSelector(
        (state: RootState) => state.settingsVideoWindow,
    );

    const { isActive: isAntiburnOsdActive } = useSelector((state: RootState) => state.antiburnOsd);
    const { visibilityState } = useSelector((state: RootState) => state.visibilityEvents);
    const isSpinnerActive = useSelector((state: RootState) => state.spinner.isSpinnerActive);
    const entryTime = React.useRef(0);
    const isScreenActive = React.useRef(false);
    const dispatch = useDispatch();
    const [isCursorActive, setisCursorActive] = useState(false);
    const [isNetworkDisconnected, setIsNetworkDisconnected] = useState(false);
    const [settingsHeaderTitle, setSettingsHeaderTitle] = useState(
        Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_DEFAULT),
    );

    const languageMap = {
        en: Locale.getString(LocaleKeys.SETTINGS_ENGLISH),
        es: Locale.getString(LocaleKeys.SETTINGS_SPANISH),
    };

    const service = new ServicesClientImpl();
    const selectedLangauge = languageMap[DeviceModel.getTVProperties().getLang()];
    const selectedVideoWindowOption = DeviceModel.getAppSettings().isVideoWindowEnabled()
        ? Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_YES)
        : Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_NO);
    const menuList = [
        Locale.getString(LocaleKeys.SETTINGS_USER_PREFERENCES),
        Locale.getString(LocaleKeys.SETTINGS_ACCOUNT),
        Locale.getString(LocaleKeys.SETTINGS_HELP),
    ];

    const accountOptionsList = [
        {
            title: Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT),
            isDisabled: isNetworkDisconnected,
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_RESET_APP),
            isDisabled: false,
        },
    ];

    const availableLanguages = [
        Locale.getString(LocaleKeys.SETTINGS_ENGLISH),
        Locale.getString(LocaleKeys.SETTINGS_SPANISH),
    ];

    const helpDetails = [
        {
            title: Locale.getString(LocaleKeys.SETTINGS_APP_VERSION),
            value: DeviceModel.getAppVersionName(),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_SHELL_APP_VERSION),
            value: DeviceModel.getTiVoSettings().getShellVersion(),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_SUPPORT),
            value: service.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_CUSTOMER_SUPPORT),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_PRIVACY_POLICY),
            value: service.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_PRIVACY),
        },
        {
            title: 'CA Device Id', // TODO: Remove this field after creating seperate page for system info.
            value: DeviceModel.getCaDeviceId(),
        },
    ];

    const userPreferedOptions = [
        {
            title: Locale.getString(LocaleKeys.SETTINGS_MENU_LANGUAGE),
            value: `[${selectedLangauge}]`,
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW),
            value: `[${selectedVideoWindowOption}]`,
        },
    ];

    const submenu = () => {
        let submenuDiv;
        switch (menuVerticalPosition) {
            case SettingsState.USER_PREFERENCE:
                submenuDiv = (
                    <Menu
                        items={userPreferedOptions}
                        selectedIndex={submenuVerticalPosition}
                        isFocused={isSubmenuFocused}
                        name={SettingsMenuTypes.SUB_MENU}
                        focusedIndex={focusedItemIndex}
                    />
                );
                break;
            case SettingsState.ACCOUNTS:
                submenuDiv = (
                    <Menu
                        items={accountOptionsList}
                        selectedIndex={submenuVerticalPosition}
                        isFocused={isSubmenuFocused}
                        name={SettingsMenuTypes.SUB_MENU}
                        focusedIndex={focusedItemIndex}
                    />
                );
                break;
            case SettingsState.HELP:
                submenuDiv = <DescriptionList items={helpDetails} />;
                break;

            default:
        }
        return <>{submenuDiv}</>;
    };

    const onCursorStateChangeCallback = (state) => {
        setisCursorActive(state);
        dispatch(onCursorStateChangeSettingsOperations());
    };

    const onNetworkStateChangeCallback = () => {
        const isDisabled = NetworkManager.isNetworkDisconnected();
        setIsNetworkDisconnected(isDisabled);
    };
    const settingsService = new Settings();
    const isVideoWindowEnabled = settingsService.isVideoWindowEnabled();

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);
        onNetworkStateChangeCallback();

        return () => {
            CursorManager.unsubscribe(refCursorManager);
            NetworkManager.unsubscribe(refNetworkManager);
        };
    }, []);

    useEffect(() => {
        setisCursorActive(DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared());
        setSettingsHeaderTitle(Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_DEFAULT));
        Diagnostics.info(TAG, 'SettingScreen mounted.');
        Log.i(TAG, 'SettingScreen mounted.');
        entryTime.current = Date.now();
        const payload: Payload = new Payload();
        payload.add(
            AnalyticsConstants.Fields.SCREEN_NAME,
            AnalyticsConstants.Screens.SETTINGS_SCREEN,
        );
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.ENTER,
        );
        VU.screen(payload);
        return () => {
            Diagnostics.info(TAG, 'SettingScreen unmounted.');
            Log.i(TAG, 'SettingScreen unmounted.');
            payload.add(
                AnalyticsConstants.Fields.SCREEN_TRANSITION,
                AnalyticsConstants.TransitionState.EXIT,
            );
            payload.add(
                AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
                Date.now() - entryTime.current,
            );
            VU.screen(payload);
        };
    }, []);

    useEffect(() => {
        if (isScreenActive && isScreenActive.current) {
            const payload: Payload = new Payload();
            payload.add(
                AnalyticsConstants.Fields.SCREEN_NAME,
                AnalyticsConstants.Screens.SETTINGS_SCREEN,
            );

            if (visibilityState === VisibilityState.ON_PAUSE) {
                payload.add(
                    AnalyticsConstants.Fields.SCREEN_TRANSITION,
                    AnalyticsConstants.TransitionState.SUSPEND,
                );
                payload.add(
                    AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
                    Date.now() - entryTime.current,
                );
            }
            if (visibilityState === VisibilityState.ON_RESUME) {
                entryTime.current = Date.now();
                payload.add(
                    AnalyticsConstants.Fields.SCREEN_TRANSITION,
                    AnalyticsConstants.TransitionState.RESUME,
                );
            }
            VU.screen(payload);
        }
        return () => {
            isScreenActive.current = true;
        };
    }, [visibilityState]);

    useEffect(() => {
        if (!isOverlayDialogActive) {
            dispatch(disableSubmenuHighlight());
        }
    }, [isOverlayDialogActive]);

    useEffect(() => {
        dispatch(setDefaultSettingsMenuStateAction(menuList.length, 0, true, 0));
        dispatch(setDefaultSettingsSubmenuStateAction(userPreferedOptions.length, false));
        setSettingsHeaderTitle(Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_DEFAULT));
        if (isMenuLanguageActive) {
            setSettingsHeaderTitle(
                Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_MENU_LANGUAGE),
            );
        }
        if (isVideoWindowActive) {
            setSettingsHeaderTitle(Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_VIDEO_WINDOW));
        }
    }, [isMenuLanguageActive, isVideoWindowActive]);

    useEffect(() => {
        switch (menuVerticalPosition) {
            case SettingsState.USER_PREFERENCE:
                dispatch(setDefaultSettingsSubmenuStateAction(userPreferedOptions.length, false));
                break;
            case SettingsState.ACCOUNTS:
                dispatch(setDefaultSettingsSubmenuStateAction(accountOptionsList.length, false));
                break;

            default:
        }
    }, [menuVerticalPosition]);

    const createUserPreferencesContent = () => {
        if (isMenuLanguageActive) {
            return <MenuLanguages languages={availableLanguages} />;
        }
        return (
            <SettingsVideoWindow
                isCursorActive={isCursorActive}
                value={selectedVideoWindowOption}
            />
        );
    };

    const menuListItems = () => {
        return (
            <>
                <div
                    className={`${
                        !isVideoWindowEnabled
                            ? 'settings-menu-container'
                            : 'settings-menu-container settings-video-window'
                    }`}
                >
                    <Menu
                        items={menuList}
                        selectedIndex={menuVerticalPosition}
                        isFocused={isFocused}
                        name={SettingsMenuTypes.MENU}
                        focusedIndex={focusedItemIndex}
                    />
                </div>
                <div
                    className={`${
                        !isVideoWindowEnabled
                            ? 'settings-submenu-container'
                            : `settings-submenu-container${menuVerticalPosition !== SettingsState.HELP ? ' settings-video-window' : ''}`
                    }`}
                >
                    {submenu()}
                </div>
            </>
        );
    };

    return (
        <div className="settings-container">
            {isSpinnerActive ? (
                <Spinner message={Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT_SPINNER_MESSAGE)} />
            ) : (
                <>
                    <SettingsHeader title={settingsHeaderTitle} />
                    <div className="settings-body">
                        {!isVideoWindowActive && !isMenuLanguageActive
                            ? menuListItems()
                            : createUserPreferencesContent()}
                    </div>
                    {isAntiburnOsdActive && <AntiburnOsd />}
                </>
            )}
        </div>
    );
};

export default SettingsScreen;
